import React from 'react';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Drawer,
  DrawerProps,
  Input,
  Row,
} from 'antd';
import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import moment from 'moment';
import { Label, SecondaryButton, message } from 'components/common';
import {
  COMMON_FLASH_MSG_DISPLAY_DURATION,
  HTTP_STATUS_OK,
  DEFAULT_US_DATE_FORMAT,
} from 'constants/config';
import { SmartSpace, playSound } from 'components/Sales/Smart/SmartComponent';
import { createLotNumber } from 'services/lot'
import { fetchLOTSettings } from 'services/lotSetting';
import { PoRecvActionType as PRAction } from './context';

type Props = ReduxStateProps & DrawerProps & {
  items: StringKAnyVPair[];
  lotNumber?: string;
  onSave?: Function;
};

const DateFormat = DEFAULT_US_DATE_FORMAT;

const CreateLotDrawer = (props: Props) => {
  const { useState } = React;
  const [expirationDate, setExpirationData] = useState<any>();
  const [firstInDate, setFirstInDate] = useState(moment());
  const [generate, setGenerate] = useState(false);
  const [inited, setInited] = useState(false);
  const [lotNumber, setLotNumber] = useState(props.lotNumber ? props.lotNumber : '');
  const [lotNumberInputVersion, setLotNumberInputVersion] = useState(0);
  //const [lotNumberGenerationType, setLotNumberGenerationType] = useState(0);
  const [manufacturingDate, setManufacturingDate] = useState<any>();
  //const [noUniqueControl, setNoUniqueControl] = useState(false);
  const [noteText, setNoteText] = useState<string>();
  const [supplierLot, setSupplierLot] = useState<string>();

  const drawerFooter = () => {
    return (
      <Row>
        <Col span={12} style={{paddingRight: 5}}>
          <SecondaryButton
            onClick={onClose}
            size="large"
            style={{width: '100%'}}
          >
            <CloseOutlined />
            Cancel
          </SecondaryButton>
        </Col>
        <Col span={12} style={{paddingLeft: 5}}>
          <Button
            disabled={!isSavable()}
            onClick={onSave}
            size="large"
            style={{width: '100%'}}
            type="primary"
          >
            <SaveOutlined />
            Save
          </Button>
        </Col>
      </Row>
    );
  };

  const isSavable = () => {
    let ret = generate;

    if (!ret) {
      ret = !!lotNumber.trim();
    }

    return ret;
  };

  // eslint-disable-next-line
  const loadInitialData = async () => {
    const { dispatch } = props;

    dispatch({ type: PRAction.SetLockScreen, params: true });
    try {
      const res = await fetchLOTSettings();

      if (res.code === HTTP_STATUS_OK && res.isSuccess) {
        //setLotNumberGenerationType(res.data.generation);
        //setNoUniqueControl(!!res.data.unique);

        if (res.data.generation === 1) {
          setGenerate(true);
        } else {
          setLotNumberInputVersion(lotNumberInputVersion + 1);
        }
      }
    } catch(e) {
      message.error({ content: `Fetched lot setting error: ${e}` });
    } finally {
      dispatch({ type: PRAction.SetLockScreen, params: false });
    }
  };

  const onChangeAutoGenerate = (evt: any) => {
    const chk = evt.target.checked;

    setGenerate(chk);
  };

  const onChangeExpirationData = (d: any) => {
    setExpirationData(d);
  };

  const onChangeFirstInDate = (d: any) => {
    setFirstInDate(d);
  };

  const onChangeLotNumber = (evt: any) => {
    setLotNumber(evt.target.value);
  };

  const onChangeManufacturingDate = (d: any) => {
    setManufacturingDate(d);
  };

  const onChangeNoteText = (evt: any) => {
    //console.log('ch', evt);
    setNoteText(evt.target.value);
  };

  const onChangeSupplierLot = (evt: any) => {
    setSupplierLot(evt.target.value);
  };

  const onClose = (evt: any) => {
    if (props.onClose) props.onClose(evt);
  };

  const onSave = async (evt: any) => {
    const { dispatch, items, state } = props;
    console.log(state.timeZone, items);

    if (!generate && !lotNumber.trim()) {
      message.error({ content: 'Please input the lot number' });
      setLotNumberInputVersion(lotNumberInputVersion + 1);
      return;
    }

    dispatch({ type: PRAction.SetLockScreen, params: true });
    try {
      const res = await createLotNumber({
        TimeZone: state.timeZone,
        Generate: generate,
        //Unique: !!unique,
        Unique: true,
        Items: props.items.map((e) => {
          return {
            SKU: e.sku,
            PolineNum: e.poLineNum,
            LotNumber: generate ? undefined : lotNumber,
            FirstInDate: firstInDate.toISOString(),
            ExpirationDate: expirationDate,
            ManufactionDate: manufacturingDate,
            SupplierLot: supplierLot,
            Note: noteText,
          };
        }),
      });

      if (res && res.code === HTTP_STATUS_OK && res.isSuccess) {
        //console.log('rd', res.data);
        message.success({ content: 'Saved successfully', duration: COMMON_FLASH_MSG_DISPLAY_DURATION });

        if (Array.isArray(res.data) && res.data.length > 0) {
          if (props.onSave) props.onSave(res.data[0]);

          // @ts-ignore
          onClose();
        }
      } else {
        playSound('error');
        message.error({ content: 'Save failed' });
      }
    } catch(e) {
      playSound('error');
      message.error({ content: `Save LOT failed: ${e}` });
    } finally {
      dispatch({ type: PRAction.SetLockScreen, params: false });
    }
  };

  React.useEffect(() => {
    if (!inited) {
      loadInitialData();
      setInited(true);
    }
  }, [inited, loadInitialData]);

  return (<>
    <Drawer
      bodyStyle={{ padding: 10 }}
      closable={false}
      footer={drawerFooter()}
      footerStyle={{ padding: 10 }}
      onClose={onClose}
      placement="bottom"
      visible={props.visible}
      width={window.innerWidth}
      height={window.innerHeight - 56}
    >
      <SmartSpace className="mb-0">
        <Row>
          <Label theme={{fontSize: '1.3em'}}>Create New LOT</Label>
        </Row>
        <div className="b-b-bordered mb-10 mt-10" />
        <Row align="middle" className="mb-5">
          <Label className="label-bold label-fs1_2 label-green1 label-required tail-colon2">LOT#</Label>
          <Checkbox checked={generate} onChange={onChangeAutoGenerate}>
            Automatically generate
          </Checkbox>
        </Row>
        {!generate && (
          <Input
            allowClear
            autoFocus
            className="fs-16"
            key={`ln-input-${lotNumberInputVersion}`}
            onChange={onChangeLotNumber}
            placeholder="Input LOT# here"
            size="large"
            value={lotNumber}
          />
        )}
        <div className="mt-10 mb-5"><Label className="label-bold label-fs1_2 label-green1 label-required tail-colon2">First In Date</Label></div>
        <DatePicker
          allowClear={false}
          defaultValue={firstInDate}
          format={DateFormat}
          onChange={onChangeFirstInDate}
          size="large"
        />
        <div className="mt-10 mb-5"><Label className="label-bold label-fs1_2 label-green1 tail-colon2">Expiration Date</Label></div>
        <DatePicker
          defaultValue={expirationDate}
          format={DateFormat}
          onChange={onChangeExpirationData}
          size="large"
        />
        <div className="mt-10 mb-5"><Label className="label-bold label-fs1_2 label-green1 tail-colon2">Manufacturing Date</Label></div>
        <DatePicker
          defaultValue={manufacturingDate}
          format={DateFormat}
          onChange={onChangeManufacturingDate}
          size="large"
        />
        <div className="mt-5">
          <Label className="label-grey">You can set the default manufacturing date to the current date upon receipt and override it to the date of manufacture at the supplier, if applicable.</Label>
        </div>
        <div className="mt-10 mb-5"><Label className="label-bold label-fs1_2 label-green1 tail-colon2">Supplier LOT#</Label></div>
        <Input
          allowClear
          //autoFocus
          className="fs-16"
          defaultValue={supplierLot}
          onChange={onChangeSupplierLot}
          placeholder="Input Supplier LOT# here"
          size="large"
        />
        <div className="mt-10 mb-5"><Label className="label-bold label-fs1_2 label-green1 tail-colon2">Note</Label></div>
        <Input.TextArea
          allowClear
          defaultValue={noteText}
          onChange={onChangeNoteText}
          placeholder="Input note here"
          size="large"
        />
      </SmartSpace>
    </Drawer>
  </>);
};

export default CreateLotDrawer;
